import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bell, CircleUser, Search, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { isAdminUser } from '../../utils/tier-utils';
import { PLANS, type Tier as PlanTier } from '../../config/plans';
import { cn } from '../../utils/cn';
import { Badge, buttonClasses } from '../ui';
import { Logo } from './Logo';
import { AccountMenu } from './AccountMenu';
import { isTabBarHidden } from './shellRoutes';

export interface AppShellProps {
  /** Renders a public header (Logo + "Anmelden") instead of nav/account when logged out. */
  allowAnonymous?: boolean;
}

interface NavItem {
  to: string;
  label: string;
}

/** Matches the desktop nav / mobile tab bar item that owns the current path. */
function isNavItemActive(pathname: string, to: string): boolean {
  if (to === '/dashboard') {
    return (
      pathname === '/dashboard' ||
      pathname.startsWith('/dashboard/subscription') ||
      pathname.startsWith('/dashboard/upgrade')
    );
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

// DashboardHome auto-redirects to /feed when push is active on this device; an explicit
// nav click must not bounce back (main's Navbar passed the same state).
const NO_REDIRECT = { noRedirect: true };

function getMobileTabItems(isAdmin: boolean) {
  const items = [
    { to: '/feed', label: 'Treffer', icon: Bell },
    { to: '/dashboard', label: 'Suchen', icon: Search },
    { to: '/dashboard/settings', label: 'Konto', icon: CircleUser },
  ];
  if (isAdmin) {
    items.push({ to: '/admin', label: 'Admin', icon: Shield });
  }
  return items;
}

/**
 * Layout route for the logged-in app: sticky header with logo, nav, plan badge
 * and account menu; a fixed mobile tab bar; and the shared <main> container.
 * Wrapped in `ProtectedRoute` for every route except `/feed`, which passes
 * `allowAnonymous` so logged-out visitors still see it (with a plain header).
 */
export const AppShell: React.FC<AppShellProps> = ({ allowAnonymous = false }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  // Called unconditionally (rules of hooks) even though the badge it feeds
  // only ever renders once `isAuthenticated` gates the nav/tab bar below.
  const unreadCount = useUnreadCount();

  if (!isAuthenticated && !allowAnonymous) {
    // ProtectedRoute already redirects logged-out visitors before this renders.
    return null;
  }

  const isAdmin = isAdminUser(user);
  const planKey = (isAdmin ? 'free' : user?.tier ?? 'free') as PlanTier;
  const planLabel = isAdmin ? 'Admin' : PLANS[planKey]?.name ?? 'Free';
  const displayUnreadCount = unreadCount > 9 ? '9+' : String(unreadCount);

  const navItems: NavItem[] = [
    { to: '/feed', label: 'Treffer' },
    { to: '/dashboard', label: 'Suchen' },
    { to: '/dashboard/settings', label: 'Einstellungen' },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  const showTabBar = isAuthenticated && !isTabBarHidden(location.pathname);

  return (
    <div className="flex min-h-screen flex-col bg-page text-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-brand focus:px-4 focus:py-2 focus:text-on-brand"
      >
        Zum Inhalt springen
      </a>

      <header className="sticky top-0 z-30 h-header shrink-0 border-b border-line bg-card/95 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="mx-auto flex h-full w-full max-w-wide items-center justify-between gap-3 px-4 md:px-6">
          <Logo to={isAuthenticated ? '/dashboard' : '/'} />

          {isAuthenticated ? (
            <>
              <nav aria-label="Hauptnavigation" className="hidden items-center gap-1 md:flex">
                {navItems.map((item) => {
                  const active = isNavItemActive(location.pathname, item.to);
                  const showUnreadBadge = item.to === '/feed' && unreadCount > 0;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      state={NO_REDIRECT}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-1.5 rounded-control px-3 py-2 text-sm font-medium text-ink hover:bg-sunken',
                        active && 'bg-brand-soft text-on-brand-soft hover:bg-brand-soft'
                      )}
                    >
                      {item.label}
                      {showUnreadBadge && (
                        <>
                          <Badge tone="signal">{displayUnreadCount}</Badge>
                          <span className="sr-only">, {unreadCount} ungelesen</span>
                        </>
                      )}
                    </Link>
                  );
                })}
              </nav>

              <div className="flex shrink-0 items-center gap-2">
                <Link to="/dashboard/upgrade" className="hidden md:inline-flex">
                  <Badge tone="neutral">{planLabel}</Badge>
                </Link>
                <AccountMenu />
              </div>
            </>
          ) : (
            <Link to="/login" className={buttonClasses('secondary', 'sm')}>
              Anmelden
            </Link>
          )}
        </div>
      </header>

      <main
        id="main"
        className={cn(
          'mx-auto w-full flex-1 px-4 pt-4 md:px-6 md:pb-12 md:pt-8',
          showTabBar ? 'pb-[calc(var(--shell-tabbar-h)+env(safe-area-inset-bottom)+16px)]' : 'pb-6'
        )}
      >
        <Outlet />
      </main>

      {showTabBar && (
        <nav
          aria-label="Hauptnavigation mobil"
          className="fixed inset-x-0 bottom-0 z-30 flex h-tabbar items-stretch border-t border-line bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          {getMobileTabItems(isAdmin).map((item) => {
            const active = isNavItemActive(location.pathname, item.to);
            const Icon = item.icon;
            const showUnreadBadge = item.to === '/feed' && unreadCount > 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                state={NO_REDIRECT}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 text-xs',
                  active ? 'text-brand' : 'text-muted'
                )}
              >
                <span className="relative">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  {showUnreadBadge && (
                    <Badge
                      tone="signal"
                      className="absolute -right-2.5 -top-1.5 px-1 py-0 text-[10px] leading-4"
                    >
                      {displayUnreadCount}
                    </Badge>
                  )}
                </span>
                <span className={active ? 'font-semibold' : undefined}>
                  {item.label}
                  {showUnreadBadge && <span className="sr-only">, {unreadCount} ungelesen</span>}
                </span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};
